import React, { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { encode } from "lib/utils/base36Encoder";
import { cx } from "lib/cx";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import LinkedinLogo from "public/icons/linkedinLogo.svg";
import WhatsappLogo from "public/icons/whatsappLogo.svg";
import GmailLogo from "public/icons/gmailLogo.svg";

interface AuthenticationProps {
  onClose: () => void;
  resumeId: number;
  handleChangeResumeVisibility: (resumeId: number, visibility: boolean) => void;
  published: boolean;
}

export const ResumePublishOptions: React.FC<AuthenticationProps> = ({
  onClose,
  resumeId,
  handleChangeResumeVisibility,
  published,
}) => {
  const [isPublic, setIsPublic] = useState(false);

  // Close the modal when clicking outside the popup
  const handleClickOutside = (event: MouseEvent) => {
    const modal = document.getElementById("auth-modal");
    if (modal && !modal.contains(event.target as Node)) {
      onClose();
    }
  };

  useEffect(() => {
    setIsPublic(published);
  }, []);

  const pathname = usePathname();
  useEffect(() => {
    localStorage.setItem("current-page", pathname);
  }, [pathname]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleResumeVisibility = (visibility: boolean) => {
    setIsPublic(visibility);
    try {
      handleChangeResumeVisibility(resumeId, visibility);
    } catch (err) {
      console.log(err);
    }
  };

  const shareableLink = `${window.location.origin}/resume/${encode(resumeId)}`;

  return (
    <section className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
      <div
        id="auth-modal"
        className="relative w-64 rounded-[42px] bg-[#FAFAFA] p-6 shadow-lg md:w-96"
      >
        {/* <button className="absolute right-2 top-2 text-xl" onClick={onClose}>
          &times;
        </button> */}

        {!resumeId ? (
          <div className="flex flex-col items-center justify-center space-y-4 text-center font-montserrat">
            <h2 className="text-xl font-semibold text-gray-700">
              Save Your Resume
            </h2>
            <p className="text-sm text-gray-500">
              Your resume is not saved yet. To get a shareable link and make it
              public or private, please save your progress.
            </p>
            <p className="font-semibold text-[#4A9FEE]">
              You can easily save it from the topbar!
            </p>
          </div>
        ) : (
          <>
            <div className="relative mx-auto flex w-48 justify-between rounded-[31px] bg-[#4A9FEE] p-1 md:w-52">
              <div
                className={cx(
                  "z-20 p-1 text-xl font-medium",
                  "flex w-1/2 cursor-pointer items-center justify-center text-center",
                  isPublic ? "text-[#646060]" : "text-white"
                )}
                onClick={() => handleResumeVisibility(true)}
              >
                Public
              </div>
              <div
                className={cx(
                  "z-20 p-1 text-xl font-medium",
                  "flex w-1/2 cursor-pointer items-center justify-center text-center",
                  !isPublic ? "text-[#646060]" : "text-white"
                )}
                onClick={() => handleResumeVisibility(false)}
              >
                Private
              </div>

              <div
                className={cx(
                  "absolute inset-y-1 z-10 w-1/2 rounded-[19px] bg-white p-1 transition-transform duration-300",
                  isPublic
                    ? "translate-x-0 transform"
                    : "translate-x-[90%] transform"
                )}
              />
            </div>

            {isPublic ? (
              <>
                <div className="mt-4 flex">
                  <div className="relative mx-auto flex items-center space-x-2 rounded-full border border-[#EEEEEE] bg-[#F9F9F9] py-2">
                    {/* URL Input */}
                    <input
                      type="text"
                      value={`${window.location.origin}/resume/${encode(
                        resumeId
                      )}`}
                      className="w-full bg-transparent text-gray-600 outline-none"
                      readOnly
                    />

                    {/* Copy Button */}
                    <button
                      className="flex items-center justify-center rounded-full bg-white p-2 shadow-md hover:bg-gray-200"
                      onClick={() =>
                        navigator.clipboard.writeText(
                          `${window.location.origin}/resume/${encode(resumeId)}`
                        )
                      }
                    >
                      {/* Copy Icon */}
                      <DocumentDuplicateIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="mx-auto mt-4 flex w-48 justify-between">
                  <Link
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                      shareableLink
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <WhatsappLogo className="h-auto w-7 cursor-pointer md:w-9" />
                  </Link>
                  <Link
                    href={`mailto:?subject=Check%20out%20my%20resume&body=${encodeURIComponent(
                      shareableLink
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <GmailLogo className="h-auto w-7 cursor-pointer md:w-9" />
                  </Link>
                  <Link
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                      shareableLink
                    )}&mini=true`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkedinLogo className="h-auto w-7 cursor-pointer md:w-9" />
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="mt-4 p-2 text-center">
                  This resume is private. <br />
                  Set it to public to get a <strong>Shareable link.</strong>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};
